//T.32832 - Componente para tracking del chofer con geolocalización y Google Maps
import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import Swal from 'sweetalert2';
import { TrackingCamionService } from '../../service/tracking-camion.service';
import { GoogleMapsHelperService } from '../../service/google-maps-helper.service';
import { GoogleMapsConfigService } from '../../service/google-maps-config.service';
import { IGarita } from '../../model/tracking-adm.model';
import { ITrackingViajes } from '../../model/modelTracking';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';

@Component({
  selector: 'app-tracking-camion',
  templateUrl: './tracking-camion.component.html',
  styleUrls: ['./tracking-camion.component.css']
})
export class TrackingCamionComponent implements OnInit, OnDestroy, AfterViewInit {

  //T.32832 - ═══════════════════════════════════════════════════════════
  //T.32832 - CONFIGURACIÓN DE PARÁMETROS CENTRALIZADOS
  //T.32832 - ═══════════════════════════════════════════════════════════
  
  //T.32832 - Configuración de Geolocalización
  private readonly CONFIG_GEOLOCATION = {
    ENABLE_HIGH_ACCURACY: true,      // Precisión alta de GPS
    TIMEOUT_MS: 10000,                // Timeout en milisegundos (10 seg)
    MAXIMUM_AGE_MS: 0,                // Edad máxima de posición cacheada (0 = siempre nueva)
    DOM_INIT_DELAY_MS: 100            // Delay para inicializar DOM del mapa
  };

  //T.32832 - Configuración de Intervalos de Actualización (en milisegundos)
  private readonly CONFIG_INTERVALS = {
    VERIFICAR_ESPACIO_MS: 60000,      // Verificar espacio disponible cada 60 segundos (1 min)
    TIMEOUT_VERIFICAR_ESPACIO_MS: 2000,  // Delay para verificar espacio (2 seg)
    TIMEOUT_PRIMERA_VERIFICACION_MS: 5000, // Delay primera verificación de espacio (5 seg)
    INVALIDATE_MAP_DELAY_MS: 100,     // Delay para invalidar tamaño del mapa
    MODAL_NOTIFICATION_DELAY_MS: 100  // Delay para mostrar modal de notificación
  };

  //T.32832 - Variables para intervalos dinámicos
  private intervaloDinamicoActual: number = 300000; // Inicialmente 5 minutos
  private descripcionIntervaloActual: string = 'Intervalo inicial';

  //T.32832 - Configuración del Mapa (Google Maps)
  private readonly CONFIG_MAPA = {
    ZOOM_INICIAL: 14,                 // Nivel de zoom inicial del mapa
    ZOOM_UBICACION_ACTUAL: 16         // Nivel de zoom al centrar en ubicación actual
  };

  //T.32832 - Configuración de Alertas (SweetAlert2)
  private readonly CONFIG_ALERTAS = {
    TIMER_VIAJE_INICIADO_MS: 3000     // Tiempo de auto-cierre para alerta de viaje iniciado
  };

  //T.32832 - Cédula del chofer (puede ser dinámico en el futuro)
  //private readonly CEDULA_CHOFER_DEFAULT = '1715771141'; 
  private readonly CEDULA_CHOFER_DEFAULT = JSON.parse(localStorage.getItem('datosUsuarioLogin')).login;
  // Para usar desde localStorage: JSON.parse(localStorage.getItem('datosUsuarioLogin')).login;

  //T.32832 - ═══════════════════════════════════════════════════════════
  //T.32832 - FIN DE CONFIGURACIÓN
  //T.32832 - ═══════════════════════════════════════════════════════════

  //T.32832 - Variables del mapa (Google Maps)
  public map: google.maps.Map | null = null;
  public garita: IGarita;
  public marcadorGarita: google.maps.Marker | null = null;
  public marcadorCamion: google.maps.Marker | null = null;
  public circuloGeofence: google.maps.Circle | null = null;
  public rutaPolyline: google.maps.Polyline | null = null;
  private marcadores: Map<string, google.maps.Marker> = new Map();

  //T.32832 - Variables de geolocalización
  public ubicacionActual: { lat: number, lng: number } | null = null;
  public distanciaGarita: number = 0;
  public enGeofence: boolean = false;
  public permisoUbicacion: boolean = false;
  public loading: boolean = false;
  public ticketAsignado: string | null = null;

  //T.32832 - Suscripciones
  private trackingSubscription: Subscription = new Subscription();
  private georeferenciaSubscription: Subscription = new Subscription();
  private watchId: number | null = null; // ID del watchPosition para geolocalización
  private verificarEspacioSubscription: Subscription = new Subscription();
  private geolocationWatchId: number | null = null;

  //T.32832 - Variables para AG-Grid
  public viajesData: ITrackingViajes[] = [];
  public columnDefs: ColDef[] = [];
  public defaultColDef: ColDef = {};
  public gridApi!: GridApi;
  public loadingViajes: boolean = false;
  
  //T.32832 - ID del viaje activo (para generar ticket)
  private viajeActivoId: number | null = null;
  
  //T.32832 - Estado del viaje activo
  private viajeActivoEstado: string | null = null;
  
  //T.32832 - Flag para evitar generar ticket duplicado
  private ticketYaGenerado: boolean = false;
  
  //T.32832 - Flag para evitar mostrar múltiples veces el mensaje de espacio disponible
  private espacioDisponibleMostrado: boolean = false;
  
  //T.32832 - Set para rastrear viajes que ya fueron notificados sobre espacio disponible
  private viajesNotificados: Set<number> = new Set<number>();

  constructor(
    private trackingService: TrackingCamionService,
    private googleMapsHelper: GoogleMapsHelperService,
    private googleMapsConfig: GoogleMapsConfigService
  ) {
    this.loading = false;
    this.garita = this.trackingService.getGarita();
    this.initializeGridConfig();
  }

  ngOnInit(): void {
    //T.32832 - Solicitar permisos de geolocalización
    this.solicitarPermisoUbicacion();
  }

  ngAfterViewInit(): void {
    //T.32832 - El mapa se inicializará después de obtener permisos GPS
    // No se inicializa aquí para evitar problemas con el DOM
  }

  ngOnDestroy(): void {
    //T.32832 - Limpia suscripciones y geolocalización
    if (this.trackingSubscription) {
      this.trackingSubscription.unsubscribe();
    }
    if (this.georeferenciaSubscription) {
      this.georeferenciaSubscription.unsubscribe();
    }
    if (this.verificarEspacioSubscription) {
      this.verificarEspacioSubscription.unsubscribe();
    }
    if (this.geolocationWatchId !== null) {
      navigator.geolocation.clearWatch(this.geolocationWatchId);
    }
  }

  //T.32832 - Solicita permiso para usar geolocalización
  public solicitarPermisoUbicacion(): void {
    if (!navigator.geolocation) {
      Swal.fire('Error', 'La geolocalización no está soportada en este navegador', 'error');
      return;
    }

    Swal.fire({
      title: 'Permiso de Ubicación',
      text: 'Esta aplicación necesita acceso a tu ubicación para mostrar tu posición en el mapa y calcular la distancia a la garita.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Permitir',
      cancelButtonText: 'Denegar',
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        console.info("Permisos autorizados para GPS");
        this.iniciarGeolocation();
      } else {
        Swal.fire('Acceso Denegado', 'No podrá ver el mapa sin permisos de ubicación', 'error');
      }
    });
  }

  //T.32832 - Inicia el seguimiento de geolocalización
  private iniciarGeolocation(): void {
    this.loading = true;
    console.info("Iniciar Geolocalizacion");
    const opciones = {
      enableHighAccuracy: this.CONFIG_GEOLOCATION.ENABLE_HIGH_ACCURACY,
      timeout: this.CONFIG_GEOLOCATION.TIMEOUT_MS,
      maximumAge: this.CONFIG_GEOLOCATION.MAXIMUM_AGE_MS
    };

    this.geolocationWatchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!this.permisoUbicacion) {
          // Primera vez que se obtiene ubicación - inicializar mapa
          this.permisoUbicacion = true;
          this.loading = false;
          
          // Esperar a que el DOM se actualice antes de inicializar el mapa
          setTimeout(() => {
            console.info("Inicia mapa y actualizacion de ubicacion");
            this.inicializarMapa();
            this.actualizarUbicacion(position);
            // Guardar la primera georeferencia inmediatamente si el viaje está EN_RUTA
            if (this.viajeActivoId && this.viajeActivoEstado === 'EN_RUTA') {
              this.guardarGeoreferencia();
            }
          }, this.CONFIG_GEOLOCATION.DOM_INIT_DELAY_MS);
        } else {
          // Actualización subsecuente
          this.actualizarUbicacion(position);
        }
      },
      (error) => {
        console.error('Error de geolocalización:', error);
        this.loading = false;
        Swal.fire('Error', 'No se pudo obtener la ubicación: ' + error.message, 'error');
      },
      opciones
    );

    //T.32832 - OPTIMIZACIÓN: Usar intervalos dinámicos basados en distancia
    // El intervalo se ajusta automáticamente según la distancia a la garita
    this.iniciarSeguimientoDinamico();

    //T.32832 - Verificar espacio disponible periódicamente para todos los viajes EN_COLA con ticket
    this.verificarEspacioSubscription = interval(this.CONFIG_INTERVALS.VERIFICAR_ESPACIO_MS).subscribe(() => {
      this.verificarEspacioParaTodosLosViajesEnCola();
    });
  }

  //T.32832 - Inicializa Google Maps
  private async inicializarMapa(): Promise<void> {
    try {
      const center: google.maps.LatLngLiteral = {
        lat: this.garita.latitud,
        lng: this.garita.longitud
      };

      //T.32832 - Inicializar mapa con el helper service (carga el script una sola vez)
      this.map = await this.googleMapsHelper.initializeMap('mapa-tracking', center);
      //T.32832 - Crea marcador de la garita
      this.crearMarcadorGarita();

      //T.32832 - Crea círculo de geofence
      this.crearCirculoGeofence();
    } catch (error) {
      console.error('T.32832 - Error al inicializar Google Maps:', error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudo cargar el mapa. Verifica la configuración de Google Maps API.',
        icon: 'error'
      });
    }
  }

  //T.32832 - Crea marcador de la garita (Google Maps)
  private crearMarcadorGarita(): void {
    if (!this.map) {
      console.error('T.32832 - No se puede crear marcador de garita: mapa no inicializado');
      return;
    }

    const position: google.maps.LatLngLiteral = {
      lat: this.garita.latitud,
      lng: this.garita.longitud
    };

    //T.32832 - Crear marcador con icono personalizado
    this.marcadorGarita = new google.maps.Marker({
      position: position,
      map: this.map,
      title: this.garita.nombre,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: '#dc3545',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2
      }
    });

    //T.32832 - Crear InfoWindow
    const infoContent = `
      <div class="text-center" style="padding: 5px;">
        <h6 style="margin: 5px 0;"><i class="fas fa-building mr-1"></i>${this.garita.nombre}</h6>
        <small>Radio: ${this.garita.radioMetros}m</small>
      </div>
    `;
    
    this.googleMapsHelper.createInfoWindow(this.marcadorGarita, infoContent);
  }

  //T.32832 - Crea círculo de geofence (Google Maps)
  private crearCirculoGeofence(): void {
    if (!this.map) {
      console.error('T.32832 - No se puede crear círculo de geofence: mapa no inicializado');
      return;
    }

    const center: google.maps.LatLngLiteral = {
      lat: this.garita.latitud,
      lng: this.garita.longitud
    };

    this.circuloGeofence = this.googleMapsHelper.createCircle(
      this.map,
      center,
      this.garita.radioMetros,
      {
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.1
      }
    );
  }

  //T.32832 - OPTIMIZACIÓN: Inicia seguimiento dinámico basado en distancia
  private iniciarSeguimientoDinamico(): void {
    // Función recursiva que se ejecuta con intervalo dinámico
    console.info("Recursividad de seguimiento dinamico");
    const ejecutarSeguimiento = () => {
      // Guardar georeferencia si hay viaje activo y tenemos ubicación
      if (this.viajeActivoId && this.ubicacionActual) {
        this.guardarGeoreferencia();
      }

      // Calcular nuevo intervalo basado en la distancia actual
      if (this.distanciaGarita > 0) {
        const resultado = this.googleMapsConfig.calcularIntervaloOptimo(this.distanciaGarita);
        this.intervaloDinamicoActual = resultado.intervalo;
        this.descripcionIntervaloActual = resultado.descripcion;
      }

      // Programar próxima ejecución con el nuevo intervalo
      setTimeout(ejecutarSeguimiento, this.intervaloDinamicoActual);
    };

    // Iniciar el primer seguimiento después de 1 segundo
    setTimeout(ejecutarSeguimiento, 1000);
  }

  //T.32832 - Actualiza la ubicación del chofer
  private actualizarUbicacion(position: GeolocationPosition): void {
    const esPrimeraUbicacion = this.ubicacionActual === null;
    
    this.ubicacionActual = {
      lat: position.coords.latitude,
      lng: position.coords.longitude
    };
    console.info("Actualiza ubicacion", this.ubicacionActual);

    //T.32832 - Actualizar marcador del camión
    this.actualizarMarcadorCamion();
    
    //T.32832 - Calcular distancia y estado
    this.calcularDistanciaYEstado();
    
    //T.32832 - Actualizar ruta
    this.actualizarRuta();
    
    //T.32832 - Centrar mapa en la ubicación actual (Google Maps)
    if (this.map && this.ubicacionActual) {
      this.googleMapsHelper.centerMap(
        this.map,
        { lat: this.ubicacionActual.lat, lng: this.ubicacionActual.lng },
        this.CONFIG_MAPA.ZOOM_UBICACION_ACTUAL
      );
    }
  }

  //T.32832 - Actualiza o crea el marcador del camión (Google Maps - OPTIMIZADO)
  private actualizarMarcadorCamion(): void {
    if (!this.map || !this.ubicacionActual) return;

    const position: google.maps.LatLngLiteral = {
      lat: this.ubicacionActual.lat,
      lng: this.ubicacionActual.lng
    };

    //T.32832 - OPTIMIZACIÓN: Solo actualizar posición si el marcador existe
    if (this.marcadorCamion) {
      this.marcadorCamion.setPosition(position);
    } else {
      //T.32832 - Crear marcador por primera vez con ícono de camión
      this.marcadorCamion = new google.maps.Marker({
        position: position,
        map: this.map,
        title: 'Mi Ubicación',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48">
              <path fill="#007bff" d="M40,18h-3v-4c0-1.1-0.9-2-2-2H13c-1.1,0-2,0.9-2,2v4H8c-1.1,0-2,0.9-2,2v12c0,1.1,0.9,2,2,2h2v4c0,1.1,0.9,2,2,2h24c1.1,0,2-0.9,2-2v-4h2c1.1,0,2-0.9,2-2V20C42,18.9,41.1,18,40,18z M13,14h22v4H13V14z M14,38c-1.1,0-2-0.9-2-2s0.9-2,2-2s2,0.9,2,2S15.1,38,14,38z M34,38c-1.1,0-2-0.9-2-2s0.9-2,2,2S35.1,38,34,38z M38,28H10v-6h28V28z"/>
              <circle fill="#ffffff" cx="14" cy="36" r="1.5"/>
              <circle fill="#ffffff" cx="34" cy="36" r="1.5"/>
            </svg>
          `),
          scaledSize: new google.maps.Size(40, 40),
          anchor: new google.maps.Point(20, 20)
        },
        optimized: true
      });

      //T.32832 - Crear InfoWindow
      const infoContent = `
        <div class="text-center" style="padding: 5px;">
          <h6 style="margin: 5px 0;"><i class="fas fa-truck mr-1"></i>Mi Ubicación</h6>
          <p style="margin: 5px 0;">Distancia a garita: <strong>${this.formatearDistancia(this.distanciaGarita)}</strong></p>
        </div>
      `;
      
      this.googleMapsHelper.createInfoWindow(this.marcadorCamion, infoContent);
    }
  }

  //T.32832 - Calcula distancia y verifica estado de geofence
  private calcularDistanciaYEstado(): void {
    if (!this.ubicacionActual) return;
    console.info("Iniciar calcular distancia y estado");
    this.distanciaGarita = this.calcularDistancia(
      this.ubicacionActual.lat,
      this.ubicacionActual.lng,
      this.garita.latitud,
      this.garita.longitud
    );

    const estadoAnterior = this.enGeofence;
    this.enGeofence = this.distanciaGarita <= this.garita.radioMetros;
    console.info("Distacia aceptable para Generar Ticket chofer ", this.enGeofence);
    //T.32832 - Verificar si debe generar ticket o verificar espacio cuando está EN_COLA y entra al geofence
    if (!estadoAnterior && this.enGeofence && !this.ticketYaGenerado) {
      if (this.viajeActivoEstado === 'EN_COLA') {
        if (!this.ticketAsignado) {
          // No tiene ticket: generarlo
          console.info("Generar Ticket Chofer");
          this.generarYMostrarTicket();
        } else {
          // Ya tiene ticket: verificar espacio directamente sin llamar georeferencia
          this.ticketYaGenerado = true; // Marcar flag para evitar reentrada
          setTimeout(() => {
            this.verificarEspacioDisponible();
          }, this.CONFIG_INTERVALS.TIMEOUT_VERIFICAR_ESPACIO_MS);
        }
      } else {
      }
    }
    
    //T.32832 - Resetear flag cuando sale del geofence
    if (estadoAnterior && !this.enGeofence) {
      this.ticketYaGenerado = false;
    }

    //T.32832 - Actualizar título del marcador
    if (this.marcadorCamion) {
      const nuevoTitulo = `Mi Ubicación - ${this.formatearDistancia(this.distanciaGarita)} - ${this.enGeofence ? 'En Garita' : 'En Ruta'}`;
      this.marcadorCamion.setTitle(nuevoTitulo);
    }
  }

  //T.32832 - Actualiza la ruta en el mapa (Google Maps - OPTIMIZADO)
  private actualizarRuta(): void {
    if (!this.map || !this.ubicacionActual) return;

    //T.32832 - OPTIMIZACIÓN: Eliminar ruta anterior si existe
    if (this.rutaPolyline) {
      this.rutaPolyline.setMap(null);
    }

    //T.32832 - Crear nueva ruta
    const path: google.maps.LatLngLiteral[] = [
      { lat: this.ubicacionActual.lat, lng: this.ubicacionActual.lng },
      { lat: this.garita.latitud, lng: this.garita.longitud }
    ];

    this.rutaPolyline = this.googleMapsHelper.createPolyline(
      this.map,
      path,
      {
        strokeColor: this.enGeofence ? '#28a745' : '#007bff',
        strokeOpacity: 0.7,
        strokeWeight: 3
      }
    );
  }

  //T.32832 - Genera y muestra el ticket llamando al backend
  private generarYMostrarTicket(): void {
    // Verificar que hay un viaje activo
    if (!this.viajeActivoId) {
      console.warn('T.32832 - No hay viaje activo para generar ticket');
      Swal.fire({
        title: 'Información',
        text: 'No hay un viaje activo para generar el ticket',
        icon: 'info',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    // Verificar que el viaje esté EN_COLA
    if (this.viajeActivoEstado !== 'EN_COLA') {
      console.warn('T.32832 - No se genera ticket porque el viaje no está EN_COLA. Estado actual:', this.viajeActivoEstado);
      return;
    }

    // Verificar que no tenga ticket asignado
    if (this.ticketAsignado) {
      console.warn('T.32832 - Ya existe un ticket asignado:', this.ticketAsignado);
      return;
    }

    // Mostrar loading mientras se genera el ticket
    Swal.fire({
      title: 'Generando Ticket',
      text: 'Por favor espera...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    // Llamar al servicio para generar el ticket
    this.trackingService.generarTicketChofer(this.viajeActivoId).subscribe({
      next: (response) => {
        // Marcar que el ticket ya fue generado
        this.ticketYaGenerado = true;
        
        // Guardar el ticket en la variable
        this.ticketAsignado = response.respuesta.choferTicket;
        
        // Iniciar verificación de espacio (solo si está EN_COLA)
        setTimeout(() => {
          this.verificarEspacioDisponible();
        }, this.CONFIG_INTERVALS.TIMEOUT_PRIMERA_VERIFICACION_MS);
        
        // T.32832 - Mensaje de llegada a garita deshabilitado por solicitud del usuario
        /*
        Swal.fire({
          title: '¡Llegada Registrada!',
          html: `<div class="text-center">
                   <h4>Ticket asignado</h4>
                   <h2 class="text-primary"><strong>${this.ticketAsignado}</strong></h2>
                   <p>Has llegado a la garita. Dirígete al punto de control.</p>
                   <hr>
                   <small><strong>Viaje:</strong> ${response.respuesta.idViaje}</small><br>
                   <small><strong>Placa:</strong> ${response.respuesta.placa}</small><br>
                   <small><strong>Estado:</strong> ${response.respuesta.estado}</small>
                 </div>`,
          icon: 'success',
          confirmButtonText: 'Entendido',
          allowOutsideClick: false,
          timer: 15000,
          timerProgressBar: true
        });
        */

        // Recargar los viajes para actualizar el estado
        this.cargarViajesActivos();
      },
      error: (error) => {
        console.error('T.32832 - Error al generar ticket:', error);
        Swal.fire({
          title: 'Error',
          text: 'No se pudo generar el ticket. Por favor intenta nuevamente.',
          icon: 'error',
          confirmButtonText: 'Entendido'
        });
      }
    });
  }

  //T.32832 - Calcula distancia entre dos puntos usando fórmula de Haversine
  private calcularDistancia(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Radio de la Tierra en metros
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  //T.32832 - Convierte grados a radianes
  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  //T.32832 - Formatea distancia para mostrar
  public formatearDistancia(distancia: number): string {
    if (distancia < 1000) {
      return `${Math.round(distancia)}m`;
    } else {
      return `${(distancia / 1000).toFixed(1)}km`;
    }
  }

  //T.32832 - Obtiene el estado actual como texto
  public getEstadoTexto(): string {
    return this.enGeofence ? 'En Garita' : 'En Ruta';
  }

  //T.32832 - Obtiene la clase CSS para el estado
  public getEstadoClase(): string {
    return this.enGeofence ? 'badge-success' : 'badge-primary';
  }

  //T.32832 - Verifica si el usuario puede iniciar un nuevo viaje (no tiene otros viajes en proceso)
  private puedeIniciarViaje(): boolean {
    if (!this.viajesData || this.viajesData.length === 0) {
      // Si aún no se han cargado viajes, asumimos que no hay viajes en proceso
      return true;
    }

    // Un viaje se considera "en proceso" si su estado es distinto de CREADO
    const viajesEnProceso = this.viajesData.filter(v => {
      const estado = (v.estado || '').toString().trim().toUpperCase();
      return estado !== 'CREADO';
    });

    console.log('T.32832 - puedeIniciarViaje - estados:', this.viajesData.map(v => v.estado), 'viajesEnProceso:', viajesEnProceso.length);

    return viajesEnProceso.length === 0;
  }

  //T.32832 - Obtiene el estilo de celda para el estado del viaje
  public getEstadoCellStyle(estado: string): any {
    // Estados "Fuera de garita" - Azul
    const estadosFueraGaritaAzul = [
      'CREADO', 
      'CONVOY_PLANIFICADO', 
      'EN_RUTA'
    ];
    
    // Estados "Dentro de garita" - Amarillo
    const estadosDentroGaritaAmarillo = [
      'REVISION_ACEPTADA',
      'REVISION_CONDICIONADA',
      'REVISION_RECHAZADA',
      'GARITA_REVISADA',
      'GARITA_APROBADO',
      'BALANZA_IN',
      'BALANZA_OUT',
      'ANDEN_ASIGNADO',
      'ANDEN_CARGANDO',
      'ANDEN_CARGADO',
      'DOCUMENTOS_INICIO',
      'DOCUMENTADO',
      'ASIGNAR_SELLOS',
      'ASIGNAR_CONVOY',
      'BALANZA_DIFERENCIA'
    ];
    
    // Determinar color de fondo
    let backgroundColor = '#6c757d'; // Gris por defecto
    let color = '#fff';
    
    if (estadosFueraGaritaAzul.includes(estado)) {
      backgroundColor = '#007bff'; // Azul
      color = '#fff';
    } else if (estadosDentroGaritaAmarillo.includes(estado)) {
      backgroundColor = '#ffc107'; // Amarillo
      color = '#212529';
    } else if (estado === 'REVISION_HABILITADA') {
      backgroundColor = '#28a745'; // Verde
      color = '#fff';
    } else if (estado === 'EN_COLA') {
      backgroundColor = '#ff4d07'; // Naranja
      color = '#fff';
    }
    
    return {
      'background-color': backgroundColor,
      'color': color,
      'padding': '4px 8px',
      'border-radius': '4px',
      'font-weight': '600',
      'text-align': 'center',
      'display': 'inline-block',
      'font-size': '0.75rem'
    };
  }

  //T.32832 - Inicializa la configuración del grid
  private initializeGridConfig(): void {
    this.columnDefs = [
      {
        headerName: 'Viaje',
        field: 'idViaje',
        width: 100,
        sortable: true
      },
      {
        headerName: 'F.Llegada',
        field: 'fechaLlegada',
        width: 120,
        sortable: true,
        valueFormatter: (params) => {
          if (params.value) {
            const date = new Date(params.value);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
          return '';
        }
      },
      {
        headerName: 'Estado',
        field: 'estado',
        width: 180,
        sortable: true,
        cellRenderer: (params: any) => {
          const estado = params.value;
          const idViaje = params.data.idViaje;
          
          // Si el estado es CREADO, mostrar botón interactivo
          if (estado === 'CREADO') {
            // Verificar si puede iniciar el viaje
            const puedeIniciar = this.puedeIniciarViaje();
            const disabled = !puedeIniciar;
            // Gris para deshabilitado, celeste/verde suave para habilitado
            const backgroundColor = disabled ? '#cccccc' : '#17a2b8';
            const cursor = disabled ? 'not-allowed' : 'pointer';
            const opacity = disabled ? '0.6' : '1';
            const title = disabled ? 'No puede iniciar este viaje. Ya tiene otro viaje en proceso.' : 'Iniciar viaje';
            
            return `<div style="display: flex; align-items: center; justify-content: center; height: 100%; padding: 2px 0;">
                      <button class="btn btn-sm" 
                              data-action="${disabled ? '' : 'iniciar-viaje'}" 
                              data-id="${idViaje}"
                              ${disabled ? 'disabled' : ''}
                              title="${title}"
                              style="background-color: ${backgroundColor}; color: #fff; padding: 2px 8px; white-space: nowrap; width: 95%; height: 26px; line-height: 1.2; border: none; font-weight: 600; cursor: ${cursor}; opacity: ${opacity};">
                        <i class="fas fa-play mr-1"></i> Iniciar Viaje
                      </button>
                    </div>`;
          }
          
          // Para otros estados, mostrar como botón con estilo según el estado
          const style = this.getEstadoCellStyle(estado);
          const backgroundColor = style['background-color'];
          const color = style['color'];
          
          return `<div style="display: flex; align-items: center; justify-content: center; height: 100%; padding: 2px 0;">
                    <button class="btn btn-sm" 
                            style="background-color: ${backgroundColor}; color: ${color}; padding: 2px 8px; font-size: 0.75rem; font-weight: 600; border-radius: 4px; white-space: nowrap; width: 95%; height: 26px; border: none; line-height: 1.2; cursor: default;">
                      ${estado}
                    </button>
                  </div>`;
        },
        onCellClicked: (params: any) => {
          const target = params.event.target;
          if (target.dataset.action === 'iniciar-viaje') {
            const idViaje = parseInt(target.dataset.id);
            this.iniciarViaje(idViaje);
          }
        }
      },
      {
        headerName: 'Ticket',
        field: 'choferTicket',
        width: 180,
        sortable: true
      },
      {
        headerName: 'Placa',
        field: 'placa',
        width: 120,
        sortable: true
      },
      {
        headerName: 'Cent Origen',
        field: 'centroOrigenNombre',
        width: 180,
        sortable: true
      },
      {
        headerName: 'Destino',
        field: 'centroDestinoNombre',
        width: 180,
        sortable: true
      },
      {
        headerName: 'F.Creación',
        field: 'creado',
        width: 150,
        sortable: true,
        valueFormatter: (params) => {
          if (params.value) {
            const date = new Date(params.value);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
          return '';
        }
      } 
    ];

    this.defaultColDef = {
      resizable: true,
      sortable: true,
      minWidth: 100
    };
  }

  //T.32832 - Evento cuando el grid está listo
  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.cargarViajesActivos();
  }

  //T.32832 - Carga los viajes activos del chofer
  private cargarViajesActivos(): void {
    this.loadingViajes = true;
    
    this.trackingService.getViajesActivosChofer(this.CEDULA_CHOFER_DEFAULT)
      .subscribe({
        next: (viajes: ITrackingViajes[]) => {
          this.viajesData = viajes;
          this.loadingViajes = false;
          // T.32832 - Seleccionar el primer viaje EN_RUTA, EN_COLA o REVISION_HABILITADA como viaje activo
          // Si ya existe un viaje activo, actualizar su estado
          let viajeActual = null;
          if (this.viajeActivoId) {
            viajeActual = viajes.find(v => v.idViaje === this.viajeActivoId);
          }
          
          const viajeEnRuta = viajes.find(v => v.estado === 'EN_RUTA');
          const viajeRevisionHabilitada = viajes.find(v => v.estado === 'REVISION_HABILITADA');
          const viajeEnCola = viajes.find(v => v.estado === 'EN_COLA');
          
          if (viajeActual) {
            // Actualizar el estado del viaje activo actual
            const estadoAnterior = this.viajeActivoEstado;
            this.viajeActivoEstado = viajeActual.estado;
            this.ticketAsignado = viajeActual.choferTicket || null;
            // Si el estado cambió de EN_COLA a REVISION_HABILITADA, resetear el flag para permitir mostrar el mensaje
            if (estadoAnterior === 'EN_COLA' && this.viajeActivoEstado === 'REVISION_HABILITADA') {
            }
            
            // Si el estado cambió de REVISION_HABILITADA a otro estado, resetear el flag
            if (estadoAnterior === 'REVISION_HABILITADA' && this.viajeActivoEstado !== 'REVISION_HABILITADA') {
              this.espacioDisponibleMostrado = false;
            }
          } else if (viajeEnRuta) {
            this.viajeActivoId = viajeEnRuta.idViaje;
            this.viajeActivoEstado = viajeEnRuta.estado;
            this.ticketAsignado = viajeEnRuta.choferTicket || null;
          } else if (viajeRevisionHabilitada) {
            this.viajeActivoId = viajeRevisionHabilitada.idViaje;
            this.viajeActivoEstado = viajeRevisionHabilitada.estado;
            this.ticketAsignado = viajeRevisionHabilitada.choferTicket || null;
          } else if (viajeEnCola) {
            this.viajeActivoId = viajeEnCola.idViaje;
            this.viajeActivoEstado = viajeEnCola.estado;
            this.ticketAsignado = viajeEnCola.choferTicket || null;
          } else if (viajes.length > 0) {
            // Si no hay viaje EN_RUTA, REVISION_HABILITADA ni EN_COLA, seleccionar el primero
            this.viajeActivoId = viajes[0].idViaje;
            this.viajeActivoEstado = viajes[0].estado;
            this.ticketAsignado = viajes[0].choferTicket || null;
          }
          
          // T.32832 - Actualizar el grid explícitamente
          if (this.gridApi) {
            this.gridApi.setRowData(this.viajesData);
          }
          
          // T.32832 - Limpiar viajes notificados que ya no están EN_COLA
          const idsViajesEnCola = viajes.filter(v => v.estado === 'EN_COLA').map(v => v.idViaje);
          this.viajesNotificados.forEach(idViaje => {
            if (!idsViajesEnCola.includes(idViaje)) {
              this.viajesNotificados.delete(idViaje);
            }
          });
          
          // T.32832 - Verificar si hay viajes EN_COLA con ticket para verificar espacio
          const viajesEnCola = viajes.filter(v => v.estado === 'EN_COLA' && v.choferTicket);
          if (viajesEnCola.length > 0) {
            viajesEnCola.forEach(viaje => {
              // Verificar espacio para cada viaje EN_COLA con ticket
              this.verificarEspacioParaViaje(viaje.idViaje, viaje.choferTicket);
            });
          } else {
          }
          
          // T.32832 - Si ya tenemos ubicación y el viaje está EN_RUTA, guardar georeferencia inmediatamente
          if (this.viajeActivoId && this.ubicacionActual && this.viajeActivoEstado === 'EN_RUTA') {
            this.guardarGeoreferencia();
          }
          
          // T.32832 - Invalidar tamaño del mapa después de mostrar el grid
          this.invalidarTamanoMapa();
        },
        error: (error) => {
          console.error('T.32832 - Error al cargar viajes:', error);
          this.loadingViajes = false;
          Swal.fire('Error', 'No se pudieron cargar los viajes activos', 'error');
        }
      });
  }

  //T.32832 - Invalida el tamaño del mapa para que se renderice correctamente (Google Maps)
  private invalidarTamanoMapa(): void {
    if (this.map) {
      // Google Maps se ajusta automáticamente, pero forzamos un resize
      setTimeout(() => {
        google.maps.event.trigger(this.map, 'resize');
      }, this.CONFIG_INTERVALS.INVALIDATE_MAP_DELAY_MS);
    }
  }

  //T.32832 - Guarda la georeferencia del vehículo en el backend
  private guardarGeoreferencia(): void {
    if (!this.ubicacionActual || !this.viajeActivoId) {
      console.warn('T.32832 - No se puede guardar georeferencia: faltan datos');
      return;
    }

    // Solo enviar georeferencia si el viaje está EN_RUTA
    if (this.viajeActivoEstado !== 'EN_RUTA') {
      return;
    }

    // Determinar el status: 1 = En ruta (fuera de geofence), 2 = En garita (dentro de geofence)
    const status = this.enGeofence ? 2 : 1;

    // Construir el objeto de georeferencia
    const georeferencia = {
      viajeId: this.viajeActivoId,
      status: status,
      fechaHora: new Date().toISOString(),
      latitud: this.ubicacionActual.lat,
      longitud: this.ubicacionActual.lng,
      distanciaGarita: this.distanciaGarita
    };
    // Llamar al servicio
    this.trackingService.guardarGeoreferencia(georeferencia).subscribe({
      next: (response) => {
        if (response) {
        }
      },
      error: (error) => {
        console.error('T.32832 - Error al guardar georeferencia:', error);
      }
    });
  }

  //T.32832 - Verifica si hay espacio disponible para el camión
  private verificarEspacioDisponible(): void {
    if (!this.viajeActivoId || !this.ticketAsignado) {
      console.warn('T.32832 - No se puede verificar espacio: falta viaje activo o ticket');
      return;
    }

    // Solo verificar espacio si el viaje está EN_COLA
    if (this.viajeActivoEstado !== 'EN_COLA') {
      return;
    }

    // Si ya se mostró el mensaje, no verificar más
    if (this.espacioDisponibleMostrado) {
      return;
    }
    this.trackingService.verificarEspacio(this.viajeActivoId).subscribe({
      next: (response) => {
        if (!response) {
          console.warn('T.32832 - ⚠️ No se recibió respuesta del servicio de verificación de espacio');
          return;
        }

        // El servicio ya extrae response.respuesta, así que response.viaje contiene el viaje directamente
        if (!response.viaje) {
          console.warn('T.32832 - ⚠️ response.viaje no existe');
          console.warn('T.32832 - ⚠️ Keys de response:', Object.keys(response));
          return;
        }

        const estadoViaje = response.viaje.estado;
        if (!estadoViaje) {
          console.warn('T.32832 - ⚠️ No se pudo obtener el estado del viaje en la respuesta');
          return;
        }

        // Verificar si el estado es REVISION_HABILITADA (hay espacio libre)
        if (estadoViaje === 'REVISION_HABILITADA') {
          // Marcar que el mensaje ya fue mostrado
          this.espacioDisponibleMostrado = true;
          // Actualizar el estado del viaje localmente
          this.viajeActivoEstado = estadoViaje;
          // Mostrar notificación al chofer de que está próximo al ingreso
          Swal.fire({
            title: '¡Está próximo su ingreso!',
            html: `<div class="text-center">
                     <h4 class="text-success"><strong>Se ha habilitado la revisión del vehiculo</strong></h4>
                     <p class="lead"><strong>Está próximo al ingreso.</strong></p>
                     <hr>
                     <div class="alert alert-info">
                       <h3 class="mb-0"><strong>Ticket: ${this.ticketAsignado}</strong></h3>
                     </div>
                   </div>`,
            icon: 'success',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#28a745',
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: true
          }).then(() => {
            // Recargar viajes para actualizar el grid con el nuevo estado
            this.cargarViajesActivos();
          });
        } else {
        }
      },
      error: (error) => {
        console.error('T.32832 - ❌ ERROR al verificar espacio:', error);
        console.error('T.32832 - Detalles del error:', JSON.stringify(error, null, 2));
      }
    });
  }

  //T.32832 - Verifica espacio para todos los viajes EN_COLA con ticket
  private verificarEspacioParaTodosLosViajesEnCola(): void {
    if (!this.viajesData || this.viajesData.length === 0) {
      return;
    }
    console.info("Verificacion de espacio para viajes en cola");
    const viajesEnCola = this.viajesData.filter(v => v.estado === 'EN_COLA' && v.choferTicket);
    
    if (viajesEnCola.length === 0) {
      return;
    }
    viajesEnCola.forEach(viaje => {
      this.verificarEspacioParaViaje(viaje.idViaje, viaje.choferTicket);
    });
  }

  //T.32832 - Verifica si hay espacio disponible para un viaje específico EN_COLA
  private verificarEspacioParaViaje(idViaje: number, ticket: string): void {
    // Si este viaje ya fue notificado, no verificar más
    if (this.viajesNotificados.has(idViaje)) {
      return;
    }
    this.trackingService.verificarEspacio(idViaje).subscribe({
      next: (response) => {
        if (!response) {
          console.warn('T.32832 - ⚠️ Response es nulo o undefined para viaje:', idViaje);
          return;
        }
        
        // El servicio ya extrae response.respuesta, así que response.viaje contiene el viaje directamente
        if (!response.viaje) {
          console.warn('T.32832 - ⚠️ response.viaje no existe para viaje:', idViaje);
          console.warn('T.32832 - ⚠️ Keys de response:', Object.keys(response));
          return;
        }
        
        const estadoViaje = response.viaje.estado;
        if (!estadoViaje) {
          console.warn('T.32832 - ⚠️ No se pudo obtener el estado del viaje');
          return;
        }
        
        // Si el estado cambió a REVISION_HABILITADA, hay espacio disponible
        if (estadoViaje === 'REVISION_HABILITADA') {
          // Marcar como notificado para evitar mostrar el mensaje múltiples veces
          this.viajesNotificados.add(idViaje);
          // Actualizar el estado del viaje si es el viaje activo
          if (this.viajeActivoId === idViaje) {
            this.viajeActivoEstado = estadoViaje;
            this.espacioDisponibleMostrado = true;
          }
          
          // Actualizar el viaje localmente en el grid ANTES de mostrar el modal
          const viajeEnGrid = this.viajesData.find(v => v.idViaje === idViaje);
          if (viajeEnGrid) {
            viajeEnGrid.estado = 'REVISION_HABILITADA';
            // Forzar actualización del grid
            if (this.gridApi) {
              this.gridApi.setRowData([...this.viajesData]);
            }
          }
          
          // Mostrar notificación al chofer
          setTimeout(() => {
            Swal.fire({
              title: '🎉 ¡Ingreso Habilitado!',
              html: `<div class="text-center">
                       <i class="fas fa-check-circle fa-4x text-success mb-3"></i>
                       <h4 class="text-success"><strong>Hay espacio disponible</strong></h4>
                       <p class="lead"><strong>Viaje ${idViaje} - Está próximo al ingreso</strong></p>
                       <p>Diríjase al punto de control con su ticket.</p>
                       <hr>
                       <div class="alert alert-info">
                         <h3 class="mb-0"><strong>Ticket: ${ticket}</strong></h3>
                       </div>
                     </div>`,
              icon: 'success',
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#28a745',
              allowOutsideClick: false,
              allowEscapeKey: false,
              allowEnterKey: true
            }).then((result) => {
              // Recargar viajes del backend para confirmar
              this.cargarViajesActivos();
            });
          }, this.CONFIG_INTERVALS.MODAL_NOTIFICATION_DELAY_MS);
        } else {
        }
      },
      error: (error) => {
        console.error('T.32832 - ❌ ERROR al verificar espacio para viaje', idViaje, ':', error);
      }
    });
  }

  //T.32832 - Inicia un viaje cambiando su estado de CREADO a EN_RUTA
  public iniciarViaje(idViaje: number): void {
    // T.32832 - Validar que no existan otros viajes en proceso
    if (!this.puedeIniciarViaje()) {
      const estadosActivos = [
        'EN_RUTA',
        'EN_COLA',
        'REVISION_HABILITADA',
        'REVISION_ACEPTADA',
        'REVISION_CONDICIONADA',
        'REVISION_RECHAZADA',
        'GARITA_REVISADA',
        'GARITA_APROBADO',
        'BALANZA_IN',
        'BALANZA_OUT',
        'ANDEN_ASIGNADO',
        'ANDEN_CARGANDO',
        'ANDEN_CARGADO',
        'DOCUMENTOS_INICIO',
        'DOCUMENTADO',
        'ASIGNAR_SELLOS',
        'ASIGNAR_CONVOY',
        'BALANZA_DIFERENCIA'
      ];

      const viajesEnProceso = this.viajesData.filter(v => estadosActivos.includes(v.estado));
      const detallesViajes = viajesEnProceso.map(v => `Viaje ${v.idViaje} - Estado: ${v.estado}`).join('<br>');
      
      Swal.fire({
        title: 'No se puede iniciar el viaje',
        html: `<p>Ya tiene ${viajesEnProceso.length} viaje(s) en proceso.</p>
               <p>Debe completar o finalizar los siguientes viajes antes de iniciar uno nuevo:</p>
               <hr>
               <div style="text-align: left; margin-top: 10px;">
                 <strong>${detallesViajes}</strong>
               </div>`,
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ffc107'
      });
      return;
    }
    
    // Confirmar acción con el usuario
    Swal.fire({
      title: '¿Iniciar Viaje?',
      text: `¿Desea iniciar el viaje ${idViaje}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, Iniciar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (result.isConfirmed) {
        // Mostrar loading
        Swal.fire({
          title: 'Iniciando Viaje',
          text: 'Por favor espera...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Llamar al servicio para actualizar el estado
        // TODO: Obtener el usuario actual del sistema de autenticación
        const modificadoPor = 'chofer'; // Temporal
        
        this.trackingService.updateEstadoViaje(idViaje, 'EN_RUTA', modificadoPor).subscribe({
          next: (response) => {
            Swal.fire({
              title: '¡Viaje Iniciado!',
              text: `El viaje ${idViaje} ha sido iniciado correctamente.`,
              icon: 'success',
              confirmButtonText: 'Entendido',
              timer: this.CONFIG_ALERTAS.TIMER_VIAJE_INICIADO_MS
            });
            
            // Recargar los viajes para actualizar el grid
            this.cargarViajesActivos();
          },
          error: (error) => {
            console.error('T.32832 - Error al iniciar viaje:', error);
            Swal.fire({
              title: 'Error',
              text: 'No se pudo iniciar el viaje. Por favor intenta nuevamente.',
              icon: 'error',
              confirmButtonText: 'Entendido'
            });
          }
        });
      }
    });
  }

  //T.32832 - Abre el PDF de instructivo Granel
  public abrirPdfGranel(): void {
    const pdfUrl = 'assets/documentos/RE-10200B - Granel.pdf';
    window.open(pdfUrl, '_blank');
  }

  //T.32832 - Abre el PDF de instructivo Contenerizado
  public abrirPdfContenerizado(): void {
    const pdfUrl = 'assets/documentos/RE-10200A - Contenerizado.pdf';
    window.open(pdfUrl, '_blank');
  }
}
